if (!customElements.get('card-images')) {
  class CardImages extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.selectors = {
        sliderWrapper: '.card-images__items',
        pagination: '.swiper-pagination',
        nextEl: '.swiper-btn-next',
        prevEl: '.swiper-btn-prev',
      };

      this.classes = {
        grid: 'f-grid',
        swiper: 'swiper',
        swiperWrapper: 'swiper-wrapper',
      };

      this.sectionId = this.dataset.sectionId;
      this.section = this.closest(`.section-${this.sectionId}`);
      this.sliderWrapper = this.querySelector(
        this.selectors.sliderWrapper
      );

      if (!this.section || !this.sliderWrapper) return;

      this.enableSlider = this.dataset.enableSlider === 'true';
      this.items = parseInt(this.dataset.items, 10) || 1;
      this.tabletItems = parseInt(this.dataset.tabletItems, 10) || 1;

      this.sliderInstance = false;
      this.totalCards = this.sliderWrapper.children.length;
      this.desktopMedia = window.matchMedia('(min-width: 1024px)');

      this.calcItemMinHeight();

      if (!this.enableSlider) return;

      this.init();
    }

    init() {
      this.updateCarouselLayout();

      this.layoutChangeHandler = () => {
        this.updateCarouselLayout();
      };

      this.desktopMedia.addEventListener(
        'change',
        this.layoutChangeHandler
      );

      this.focusHandler = (event) => {
        const slider = this.sliderInstance?.slider;

        if (!slider || slider.destroyed) return;

        const slide = event.target.closest('.swiper-slide');

        if (!slide || !this.contains(slide)) return;

        const index = Array.from(slider.slides).indexOf(slide);

        if (index >= 0) {
          slider.slideTo(index);
        }
      };

      this.addEventListener('focusin', this.focusHandler);

      if (window.Shopify && Shopify.designMode) {
        this.blockSelectHandler = (event) => {
          if (event.detail.sectionId != this.sectionId) return;

          const slider = this.sliderInstance?.slider;

          if (!slider || slider.destroyed) return;

          const block = event.target.closest('[data-index]');

          if (!block || !this.contains(block)) return;

          const index = Number(block.dataset.index);

          if (Number.isFinite(index)) {
            slider.slideToLoop(index);
          }
        };

        document.addEventListener(
          'shopify:block:select',
          this.blockSelectHandler
        );
      }
    }

    updateCarouselLayout() {
      const useStaticGrid =
        this.desktopMedia.matches &&
        this.totalCards <= this.items;

      if (useStaticGrid) {
        this.destroySlider();
        this.classList.add('card-images--static');
        return;
      }

      this.classList.remove('card-images--static');
      this.initSlider();
    }

    initSlider() {
      if (
        this.sliderInstance &&
        this.sliderInstance.slider &&
        !this.sliderInstance.slider.destroyed
      ) {
        return;
      }

      const columnGap = FoxTheme.utils.getGridColumnGap(
        this.sliderWrapper
      );

      const sliderOptions = {
        slidesPerView: 1.15,
        spaceBetween: 12,

        navigation: {
          nextEl: this.section.querySelector(this.selectors.nextEl),
          prevEl: this.section.querySelector(this.selectors.prevEl),
        },

        pagination: {
          el: this.section.querySelector(this.selectors.pagination),
          clickable: true,
        },

        breakpoints: {
          1024: {
            slidesPerView:
              this.tabletItems > 3
                ? 3
                : parseInt(this.tabletItems, 10),
            spaceBetween: columnGap.tabletLarge,
          },

          1280: {
            slidesPerView: parseInt(this.items, 10),
            spaceBetween: columnGap.desktop,
            allowTouchMove: this.totalCards > this.items,
          },
        },

        loop: true,
        threshold: 2,
      };

      this.classList.add(this.classes.swiper);
      this.sliderWrapper.classList.remove(this.classes.grid);
      this.sliderWrapper.classList.add(
        this.classes.swiperWrapper
      );

      this.sliderInstance = new window.FoxTheme.Carousel(
        this,
        sliderOptions
      );

      this.style.setProperty(
        '--swiper-autoplay-delay',
        `${Math.max(
          2000,
          Number(this.dataset.autoplaySpeed) || 5000
        )}ms`
      );

      this.sliderInstance.init();
      this.startAutoplay();
    }

    startAutoplay() {
      clearInterval(this.autoplayTimer);

      if (this.dataset.autoplay !== 'true') return;

      const delay = Math.max(
        2000,
        Number(this.dataset.autoplaySpeed) || 5000
      );

      this.autoplayTimer = setInterval(() => {
        const slider = this.sliderInstance?.slider;

        if (
          !this.isConnected ||
          !slider ||
          slider.destroyed ||
          this.classList.contains('card-images--static') ||
          document.hidden ||
          this.matches(':hover') ||
          this.contains(document.activeElement)
        ) {
          return;
        }

        if (
          this.dataset.autoplayDirection === 'left_to_right'
        ) {
          slider.slidePrev();
        } else {
          slider.slideNext();
        }
      }, delay);
    }

    disconnectedCallback() {
      clearInterval(this.autoplayTimer);

      if (this.desktopMedia && this.layoutChangeHandler) {
        this.desktopMedia.removeEventListener(
          'change',
          this.layoutChangeHandler
        );
      }

      if (this.focusHandler) {
        this.removeEventListener('focusin', this.focusHandler);
      }

      if (this.blockSelectHandler) {
        document.removeEventListener(
          'shopify:block:select',
          this.blockSelectHandler
        );
      }

      if (this.sliderWrapper) {
        this.destroySlider();
      }
    }

    destroySlider() {
      clearInterval(this.autoplayTimer);

      const slider = this.sliderInstance?.slider;

      if (slider && !slider.destroyed) {
        slider.destroy(true, true);
      }

      this.sliderInstance = false;

      this.classList.remove(this.classes.swiper);
      this.sliderWrapper.classList.remove(
        this.classes.swiperWrapper
      );
      this.sliderWrapper.classList.add(this.classes.grid);
    }

    /**
     * Make sure overlay content displays properly
     * without overlapping the image.
     */
    calcItemMinHeight() {
      let maxHeight = 0;
      const cardMedias = this.querySelectorAll('.card-media');

      cardMedias.forEach((cardMedia) => {
        const cardContent = cardMedia.querySelector(
          '.card-media__content'
        );

        if (!cardContent) return;

        const cardContentInner = cardContent.querySelector(
          '.card-media__content-wrapper'
        );

        if (!cardContentInner) return;

        const cardContentPadding =
          this.getElementPadding(cardContent);

        let itemHeight = cardContentInner.offsetHeight;

        if (cardContentPadding) {
          itemHeight +=
            cardContentPadding.top + cardContentPadding.bottom;
        }

        if (itemHeight > maxHeight) {
          maxHeight = itemHeight;
        }

        cardMedia.style.setProperty(
          '--item-height',
          itemHeight + 'px'
        );
      });

      this.style.setProperty(
        '--items-height',
        maxHeight + 'px'
      );
    }

    getElementPadding(element) {
      if (!element) {
        return null;
      }

      const style = window.getComputedStyle(element);
      const paddingTop = parseFloat(style.paddingTop);
      const paddingBottom = parseFloat(style.paddingBottom);

      return {
        top: paddingTop,
        bottom: paddingBottom,
      };
    }
  }

  customElements.define('card-images', CardImages);
}